<script>
import { startOfWeek, endOfWeek } from 'date-fns'
import formatDate from '@utils/format-date'
import lazyLoadComponent from '@router/lazyload-component'
import { genderFilter, bookingStatusFilter, orderStatusFilter, bookingStatusMap } from '@utils/filter-value-to-text'

import format from 'date-fns/format'

import VueCal from 'vue-cal'
import 'vue-cal/dist/i18n/zh-hk.js'
import 'vue-cal/dist/vuecal.css'

export default {
  page: {
    title: '行事曆',
    meta: [{ name: 'description', container: 'schedule' }],
  },
  components: {
    VueCal,
    CalendarItem: () => lazyLoadComponent(import('./CalendarItem')),
    WeekPicker: () => lazyLoadComponent(import('@components/WeekPicker')),
    EmployeeReservationOrderDetail: () => lazyLoadComponent(import('@components/Orders/EmployeeReservationOrderDetail')),
    RemarkDialog: () => lazyLoadComponent(import("@router/views/schedule/components/RemarkDialog")),
  },
  filters: {
    genderFilter,
    orderStatusFilter,
  },
  props: {
    mode: {
      type: String,
      default: 'week',
    },
  },
  data() {
    return {
      selectedDate: new Date(),
      events: [],
      tryingToFetchSchedule: false,

      editable: {
        title: false,
        drag: false,
        resize: false,
        create: false,
        delete: false,
      },

      // 營業時間
      openingHour: '00:00',
      closedHour: '23:59',

      tryingToGetOrder: false,
      drawerOrderData: {},
      drawerVisible: false,
      triggerIndex: 0,
      canChangeDate: false,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
      remarkContextMenuStatus: false,
      openRemarkInfo: {}, // 此訂單的基本資訊（要拿去打開備註視窗使用）
      columnShowList: ['客戶備註', '寵物備註', '服務備註', '使用人備註'],
      isSpace: 0
    }
  },
  computed: {
    selectedDateString() {
      return this.selectedDate.format()
    },
    headerDate() {
      let date = this.$refs.cal ? this.$refs.cal.view.startDate : new Date()
      if (this.$route.query.type === 'order' && !this.canChangeDate) {
        this.canChangeDate = true
        date = new Date(this.drawerOrderData.date)
      }
      const trigger = this.triggerIndex % 2 === 0 ? '\n' : ''
      if (this.isWeekMode) return `${format(startOfWeek(date, { weekStartsOn: 1 }), 'YYYY年MM月DD日')} ~ ${format(endOfWeek(date, { weekStartsOn: 1 }), 'YYYY年MM月DD日')}${trigger}`
      else return format(date, 'YYYY年MM月')
    },
    isWeekMode() {
      return this.mode === 'week'
    },
    isMobile() {
      return /Mobi/.test(navigator.userAgent)
    },
  },
  watch: {
    selectedDateString() {
      this.scrollToNow()
    }
  },
  created() {
    this.tryToFetchSchedule()
    this.tryToGetOrderByQuery()
  },
  mounted() {
    this.getVueCalHeight()
    this.$nextTick(() => {
      setTimeout(() => {
        this.scrollToNow()
      }, 100)
    })
  },
  methods: {
    /**
     * @description 述說要顯現什麼狀態
     */
    bookingStatusText(item) {
      return bookingStatusMap[item]['text']
    },
    hasPrepaidParams(params) {
      return params.some(item => {
        return item.type === 'prepaid'
      })
    },
    /**
   * @description 開啟查看備註
   */
    async openRemarkDialog(event) {
      this.remarkContextMenuStatus = true
      let orderInfo = await this.$store.dispatch('order/fetchSingle', event.orderId)
      this.openRemarkInfo = orderInfo
    },
    /**
     * @description 關閉查看備註
     */
    clickCloseRemarkFunc() {
      this.remarkContextMenuStatus = false
    },
    addItems(order) {
      let list = []
      order.params.forEach(item => {
        if (item.type === 'cosmetic-add') {
          list.push(item)
        }
      })
      order.childrenOrders.forEach(item => {
        item.items.forEach(i => {
          if (i.type === 'cosmetic-add') {
            list.push(i)
          }
        })

      })
      return list
    },
    bookingStatusFilter,
    async tryToFetchSchedule() {
      this.tryingToFetchSchedule = true
      const { startDate: date, endDate } = this.getWeekStartEnd()
      const result = await this.$store.dispatch('schedule/fetchStaffDetail', {
        id: this.$store.state.auth.currentUser.scheduleItemId,
        date
      })
      const isSpace = result.type === 'space' ? 1 : 0
      const list = await this.$store.dispatch('schedule/fetchRangeList', {
        date,
        endDate,
        types: ['cosmetic', 'daycare'],
        scheduleItemTypes: isSpace ? ['space'] : ['cosmetic'],
        path: this.$route.path
      })
      this.isSpace = isSpace
      this.filterEvents(list.items)
      this.tryingToFetchSchedule = false
    },
    async tryToGetOrderByQuery() {
      if (this.$route.query.type === 'order') {
        await this.tryToGetOrder(this.$route.query.id)
        const date = this.drawerOrderData.date
        this.selectedDate = new Date(date)
      }
    },
    async tryToGetOrder(orderId) {
      this.tryingToGetOrder = true
      this.drawerOrderData = await this.$store.dispatch('order/fetchSingle', orderId)
      // console.log(this.drawerOrderData)
      this.tryingToGetOrder = false
      this.drawerVisible = true
    },
    filterEvents(list) {
      this.events = list.map((order) => ({
        start: order.startDate,
        end: order.endDate,
        customInfo: { ...order },
      }))
    },
    fetchEvents() {
      this.tryToFetchSchedule()
    },
    getWeekStartEnd() {
      if (this.$refs.cal) {
        const { startDate, endDate } = this.$refs.cal.view
        return { startDate: formatDate(startDate, 'date-dash'), endDate: formatDate(endDate, 'date-dash') }
      } else {
        const startDate = formatDate(startOfWeek(new Date(), { weekStartsOn: 1 }), 'date-dash')
        const endDate = formatDate(endOfWeek(new Date(), { weekStartsOn: 1 }), 'date-dash')
        return { startDate, endDate }
      }
    },
    onClickPreviousDay() {
      this.$refs.cal.previous()
      if (this.isWeekMode) {
        this.$refs.week.weekPre()
      } else {
        this.selectedDate = this.$refs.cal.view.startDate
        const date = formatDate(this.$refs.cal.view.startDate, 'date-dash')
        this.$refs.week.initData(date)
      }
      this.triggerIndex++
    },
    onClickNextDay() {
      this.$refs.cal.next()
      if (this.isWeekMode) {
        this.$refs.week.weekNext()
      } else {
        this.selectedDate = this.$refs.cal.view.startDate
        const date = formatDate(this.$refs.cal.view.startDate, 'date-dash')
        this.$refs.week.initData(date)
      }
      this.triggerIndex++
    },
    onClickToday() {
      this.selectedDate = new Date()
      this.isWeekMode && this.$refs.week.initData(this.selectedDateString)
      this.triggerIndex++
    },
    checkExpired(date) {
      return new Date().getTime() > new Date(date)
    },
    onClickDetail(event) {
      this.tryToGetOrder(event.customInfo.orderId)
    },
    updateSelectedDate(date) {
      this.selectedDate = date
      this.isWeekMode && this.$refs.week.initData(this.selectedDateString)
      this.triggerIndex++
    },
    showDatePicker() {
      this.$refs.picker.showPicker()
    },
    onDatePickerChange() {
      this.$refs.week.initData(this.selectedDateString)
      this.triggerIndex++
    },
    getVueCalHeight() {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const safeAreaInsetTop = getComputedStyle(document.documentElement).getPropertyValue("--sat").replace('px', '');

      this.$nextTick(() => {
        if (windowWidth < 481 && !this.isWeekMode) {
          this.$refs.cal.$el.querySelector('.vuecal__bg').style.height = `${windowHeight - 355 - safeAreaInsetTop}px`
        }
        if (windowWidth >= 481) {
          this.$refs.cal.$el.querySelector('.vuecal__bg').style.height = `auto`
        }
      })
    },
    scrollToNow() {
      const now = new Date()
      const minutes = (now.getHours() - 6) * 60 + now.getMinutes()
      const pxFerMinute = 50 / 15
      if (now.format() === this.selectedDateString) {
        this.$refs.cal.$el.querySelector('.vuecal__bg').scrollTop = minutes * pxFerMinute - 50
      } else {
        this.$refs.cal.$el.querySelector('.vuecal__bg').scrollTop = 600
      }

    },
  },
}
</script>

<template>
  <div v-loading.fullscreen.lock="tryingToGetOrder" element-loading-text="取得訂單資訊中">
    <el-row :gutter="isMobile ? 0 : 10">
      <el-col :span="17" :xs="24">
        <el-card shadow="never" :class="$style['card-wrapper']">
          <template v-if="!isMobile" slot="header">
            <div>
              <el-button class="is-round" @click="onClickToday">今天</el-button>
            </div>
            <div class="Jost" :class="$style['header-date']">
              {{ headerDate }}
            </div>
            <div :class="$style['header-nav']">
              <el-button class="is-round" @click="onClickPreviousDay">
                <BaseIcon icon-style="fal" name="chevron-left"></BaseIcon>
              </el-button>
              <el-button class="is-round" @click="onClickNextDay">
                <BaseIcon icon-style="fal" name="chevron-right"></BaseIcon>
              </el-button>
            </div>
          </template>
          <template v-else slot="header">
            <BaseIcon icon-style="fal" name="chevron-left" @click.native="onClickPreviousDay"></BaseIcon>
            <div v-if="headerDate" class="Jost" :class="$style['header-date']" @click="showDatePicker">
              <BaseIcon icon-style="fal" name="calendar"></BaseIcon> {{ headerDate }}
              <el-date-picker ref="picker" v-model="selectedDate" type="date" format="yyyy/MM/dd"
                :class="$style['mobile-picker']" @change="onDatePickerChange"></el-date-picker>
            </div>
            <BaseIcon icon-style="fal" name="chevron-right" @click.native="onClickNextDay"></BaseIcon>
          </template>
          <WeekPicker v-if="isWeekMode" ref="week" :class="$style['weekpicker']" :format-string="'D'"></WeekPicker>
          <WeekPicker v-else ref="week" class="visible-mobile" :class="$style['weekpicker']" :format-string="'D'"
            :show-selected="true" :selected-date.sync="selectedDate" @change="triggerIndex++"></WeekPicker>
          <div v-loading="tryingToFetchSchedule" element-loading-text="取得行事曆資訊中"
            :class="[$style['vue-cal-layout'], { topMargin: !isWeekMode }]">
            <div class="schedule-wrap">
              <VueCal ref="cal" class="full-cal vuecal--full-height-delete"
                :class="[[$style.schedule], $style['is-week']]" :active-view="mode" locale="zh-hk"
                :selected-date="selectedDate" :time-from="6 * 60" :time-to="24 * 60" :sticky-split-labels="true"
                :editable-events="editable" :events="events" :time-step="15"
                :disable-views="['years', 'year', 'week', 'day', 'month']" :hide-title-bar="true" :time-cell-height="50"
                :snap-to-time="1" @view-change="fetchEvents">
                <template v-slot:event="{ event }">
                  <div v-if="!event.disabled" class="wrap"
                    :class="[
                      bookingStatusFilter(event.customInfo.orderStatus, 'color'), 
                      { 'is-expired': checkExpired(event.end) },
                    ]"
                    style="height: inherit;">
                    <div :class="$style.mainBlock" @click="onClickDetail(event)">
                      <div class="topIcon">
                        <div v-if="hasPrepaidParams(event.customInfo.params)" class="info-icon isPrepaid">
                          方案抵扣
                        </div>
                        <div v-else class="info-icon">
                          單次預約
                        </div>
                        <div class="clientStatus">{{ bookingStatusText(event.customInfo.orderStatus) }}</div>
                      </div>
                      <div class="contextPart">
                        <div class="nameOuterDisplay">
                          <!-- 名字 -->
                          <div class="name">
                            <div>
                              {{ event.customInfo.pet.name }} - {{ event.customInfo.customerName }} {{
                                event.customInfo.customerGender | genderFilter
                              }}
                            </div>
                          </div>
                          <!-- 等級title -->
                          <div v-if="$hasMemberLevel() && event.customInfo.level" class="levelTitle">
                            {{ event.customInfo.level.title }}
                          </div>
                          <div v-if="!!event.customInfo.tags && event.customInfo.tags.length > 0" class="tagsGroup">
                            <div class="text-muted-0" style="margin-bottom:10px">標籤</div>
                            <div class="tagsBody">
                              <span v-for="(item, index) in event.customInfo.tags" :key="index" class="tagsTitle">
                                {{ item }}
                              </span>
                            </div>
                          </div>
                        </div>
                        <!-- 時間 -->
                        <div class="title" :event="event">
                          <div class="time" style="padding-right:10px">{{ event.start.format('HH:mm') + ' ~ ' +
                            event.end.format('HH:mm')
                          }}</div>
                          <span v-if="event.customInfo.bufferTime > 0" class="text-muted-0">(緩衝時間 {{
                            event.customInfo.bufferTime
                          }} 分鐘)</span>
                        </div>
                        <!-- 服務項目 -->
                        <div class="text-muted-0" style="margin-bottom:10px">服務項目</div>
                        <div class="serviceTitle" style="margin-bottom:6px">{{ `${event.customInfo.productTitle}
                                                  ${event.customInfo.priceTitle}`
                        }}</div>
                        <div class="serviceTitle" style="margin-bottom:6px">{{ `${event.customInfo.priceCategory}
                                                  ${event.customInfo.pet.breed ?
                            '- ' + event.customInfo.pet.breed : ''} | ${event.customInfo.priceIntrotext}`
                        }}</div>
                        <!-- 加購項目 -->
                        <template v-if="addItems(event.customInfo).length">
                          <div class="addBlock">
                            <div class="text-muted-0" style="margin-bottom:10px;margin-top:10px">
                              加購項目
                            </div>
                            <div v-for="(item, index) in addItems(event.customInfo)" :key="index">
                              <div class="serviceTitle" style="margin-bottom:6px">{{ item.title }} <span
                                  class="text-muted-0"> -
                                  {{ item.serviceTime }} 分鐘</span>
                                <span v-if="item.paymentStatus === 'PAID'" class="addPaid"> 已付款</span>
                                <span v-if="item.paymentStatus === 'UNPAID'" class="addUnPaid"> 未付款</span>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                      <template>
                        <div class="remark">
                          <div class="remarkInner">
                          </div>
                        </div>
                      </template>
                    </div>
                    <div :class="$style.outerMainButton">
                      <el-popover popper-class="popoverPadding" placement="right" width="300" trigger="click">
                        <RemarkDialog :column-show-list="columnShowList.filter(item => item !== '會員等級' || item !== '標籤')"
                          :open-remark-info="openRemarkInfo" />
                        <el-button slot="reference" @click="openRemarkDialog(event.customInfo)" class="is-no-style">
                          <BaseIcon name="eye" />
                          <p>查看備註</p>
                        </el-button>
                      </el-popover>
                    </div>
                  </div>
                  <div v-else class="disabled"></div>
                </template>
              </VueCal>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="7" class="hidden-mobile">
        <CalendarItem @update="updateSelectedDate"></CalendarItem>
      </el-col>
    </el-row>
    <el-drawer ref="orderFormEdit" class="only-close isStyleOrderDetail" :class="{ 'appStyle': appStyle }"
      :visible.sync="drawerVisible" append-to-body :size="$root.isSmallDevice ? '100%' : '90%'" destroy-on-close
      :wrapper-closable="false" custom-class="order-drawer">
      <EmployeeReservationOrderDetail v-if="drawerVisible" ref="orderDetail" :is-space="isSpace" order-type="reservation"
        :default-value.sync="drawerOrderData" is-employee @closeDrawer="drawerVisible = false"
        @update="tryToFetchSchedule" />
    </el-drawer>
  </div>
</template>
<style lang="scss"></style>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.vue-cal-layout {
  display: flex;
  margin-left: -25px;
  padding-left: 22px;

  &:global {
    &.topMargin {


      @include touch {
        background-color: white;
      }
    }
  }

  @include touch {
    margin-right: -21px;
    margin-top: 0;
    padding-left: 15px;
    padding-right: 10px;
    padding-top: 2px;
  }

  :global {
    .vuecal__weekdays-headings {
      display: none;
    }

    .schedule-wrap {
      flex: 1 0 auto;
      height: auto;
    }
  }
}

.schedule {
  border-radius: 4px;
  box-shadow: inherit !important;
  position: relative;

  :global {
    .vuecal__header {
      .vuecal__split-days-headers {
        border-top: 1px solid #ebeef5;
        border-bottom: 1px solid #ebeef5;

        .day-split-header {
          .el-dropdown {
            width: 99%;

            button {
              border: none;
              width: 100%;

              span {
                display: flex;
                justify-content: space-between;
              }
            }
          }

          &+.day-split-header {
            border-left: 1px solid rgb(198 198 186 / 0.7);
          }
        }
      }
    }

    .vuecal__time-cell-line::before {
      left: 43px;
    }

    .vuecal__body:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 42px;
      width: 100%;
      height: 1px;
      background: rgba(0, 0, 0, 0.08);
    }

    .vuecal__cell-content {
      .vuecal__no-event {
        display: none;
      }

      .vuecal__event {
        background-color: transparent;
        cursor: pointer;

        .wrap {
          padding: 20px 20px 10px;
          border-radius: 8px;
          border-width: 2px;
          border-style: solid;
          text-align: left;
          overflow-y: auto;

          .title {
            .time {
              .cycle {
                font-weight: 400;
              }
            }

            svg {
              color: white;
              background: green;
              padding: 6px;
              font-size: 30px;
              border-radius: 6px;
              padding-left: 8px;
            }
          }

          // 已完成
          &.finish {
            border-color: $color-sched-state-finish;
            background-color: #F0FCFF;

            .addBlock,
            .remark {
              border-color: $color-sched-state-finish;
            }

            .clientStatus {
              color: darken($color-sched-state-finish, 35%);
              border: 1px solid darken($color-sched-state-finish, 35%);
            }

          }

          // 已報到
          &.success {
            border-color: $color-sched-state-success;
            background-color: #F2FFFB;

            .addBlock,
            .remark {
              border-color: $color-sched-state-success;
            }

            .clientStatus {
              color: darken($color-sched-state-success, 35%);
              border: 1px solid darken($color-sched-state-success, 35%);
            }
          }

          // 待報到
          &.warning {
            border-color: $color-sched-state-warning;
            background-color: #FFF9F4;

            .addBlock,
            .remark {
              border-color: $color-sched-state-warning;
            }

            .clientStatus {
              color: darken($color-sched-state-warning, 35%);
              border: 1px solid darken($color-sched-state-warning, 35%);
            }
          }

          // 未報到
          &.danger {
            border-color: $color-sched-state-danger;
            background-color: #FFF4F4;

            .addBlock,
            .remark {
              border-color: $color-sched-state-danger;
            }

            .clientStatus {
              color: darken($color-sched-state-danger, 35%);
              border: 1px solid darken($color-sched-state-danger, 35%);
            }
          }

          // 未付款
          &.info {
            border-color: $color-sched-state-info;
            background-color: lighten($color-sched-state-info, 10%);

            .addBlock,
            .remark {
              border-color: $color-sched-state-info;
            }

            .clientStatus {
              color: darken($color-sched-state-info, 35%);
              border: 1px solid darken($color-sched-state-info, 35%);
            }
          }

          &.is-disabled {
            pointer-events: none;
          }

          &.is-expired,
          &.is-disabled {
            &::before {
              content: '';
              position: absolute;
              top: -10px;
              bottom: -10px;
              left: -10px;
              right: -10px;
              background: white;
              opacity: 0.1;
            }
          }
        }

        &.vuecal__event--background {
          margin-top: 2px;
          z-index: 0;

          .disabled {
            height: inherit;
            background: repeating-linear-gradient(45deg, lightgrey 0px, lightgrey 2px, #f2f2f2 0px, #f2f2f2 7px);
            opacity: 0.5;
            box-shadow: inset 0 -2px white;
            pointer-events: none;
          }
        }
      }

      &+.vuecal__cell-content {
        border-left: 1px solid rgba(0, 0, 0, 0.08);
      }

      .vuecal__cell-events {
        .vuecal__event {
          @include touch {
            width: 99% !important;
            margin-left: 2px;
          }
        }
      }
    }

    .vuecal__time-column {
      @include touch {
        background-color: white;
      }

      // width: 4.4em;
    }
  }
}

:global() {
  :local(.schedule) {
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 42px;
      width: calc(100% - 9px);
      height: 1px;
      background: rgb(0 0 0 / 0.08);
    }
  }
}

.card-wrapper {
  min-height: 79vh;

  @include touch {
    min-height: 0;
    margin-bottom: 0;
  }

  :global {
    .el-card__header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      @include touch {
        background: white;
        border-top: none;
        border-bottom: none;
        height: 48px;
      }
    }

    .el-card__body {
      position: relative;

      @include touch {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }
}

.status {
  font-size: 14px;
  line-height: 20p;
  color: #3f3a2e;
  font-weight: 500;
  letter-spacing: 1.4px;
}

.weekpicker {
  padding-bottom: 24px;
  margin: 0 -20px 0px -20px;
  border-bottom: 1px solid #ebeef5;
  padding-left: 57px;
  padding-right: 20px;

  @include touch {
    background: white;
    margin: 0 -20px;
    margin-top: 0 !important;
    padding-bottom: 20px;
    padding-top: 25px;
    padding-left: 10px;
    padding-right: 10px;
  }

  :global {
    ul {
      height: auto;
    }

    li {
      border: none !important;

      .item-wrapper {
        .selected {
          box-shadow: 0 0 0 2px $color-primary;
        }

        p {
          margin-top: 0;
          color: $color-primary;
          margin-bottom: 20px;

          @include touch {
            font-size: 12px;
            // color: #98b8ad;
          }
        }

        span {
          color: #3f3a2e;
          font-weight: 500;
          width: 35px;
          height: 35px;
          border-radius: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      &.today {
        background: transparent !important;
        color: inherit !important;

        .item-wrapper {
          span {
            color: white;
            background: $color-primary;
            // background: #41695b;
          }
        }
      }
    }
  }
}

.is-week {
  :global {

    .vuecal__cell--current,
    .vuecal__cell--today {
      background-color: rgba(235, 255, 245, 0.4);
    }

    .vuecal__cell--selected {
      background-color: rgba(240, 240, 255, 0.4);
    }
  }
}

.header-date {
  position: relative;
  font-size: 16px;
  font-weight: 500;

  svg {
    margin-right: 10px;
  }
}

.header-nav {
  button {
    padding: 12px !important;
    font-size: 12px;
  }
}

.mobile-picker {
  width: 0;
  position: absolute;
  left: 0;
  height: 24px;
  z-index: -1;
}

.mainBlock {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  height: 80%;

  :global {

    .contextPart {
      overflow: auto;
      padding-bottom: 5px;
    }

    .nameOuterDisplay {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 8px;
      margin-top: 8px;

      .levelTitle {
        border-radius: $border-radius;
        background-color: $color-line-dark;
        padding: 5px 10px;
        font-size: 14px;
        text-align: center;
        margin-right: 10px;
        white-space: nowrap;
        margin-bottom: 5px;
        display: inline-block;
        margin-top: 5px;
      }

      .tagsGroup {
        display: flex;
        margin-top: 10px;
        flex-direction: column;
        height: auto;
        flex-wrap: wrap;

        .tagsBody {
          display: flex;
          flex-wrap: wrap;

          .tagsTitle {
            border-radius: $border-radius;
            background-color: $color-line-dark;
            padding: 5px 10px;
            font-size: 14px;
            text-align: center;
            margin-right: 10px;
            white-space: nowrap;
            margin-bottom: 5px;
          }
        }

      }

      .name {
        font-size: 16px;
        letter-spacing: 1.4px;
        line-height: 20px;
        color: #3f3a2e;
        margin-bottom: 5px;
        font-weight: 400;
        display: flex;
      }
    }

    .time {
      display: inline-block;
    }

    .title {
      margin-top: 0 !important;
      display: block !important;
      margin-bottom: 15px
    }

    .serviceTitle {
      color: $color-secondary-light;
    }

    .topIcon {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      .info-icon {
        color: #fff;
        background-color: $color-primary;
        display: inline-block;
        padding: 7px 10px;
        font-weight: 300;
        font-size: 14px;
        margin-right: 10px;

        &.isPrepaid {
          background-color: #2D385F;
        }
      }

      .clientStatus {
        border-radius: $border-radius;
        padding: 5px 12px;
      }
    }


    .addBlock {
      border-top: $color-text-light 1px solid;
      margin-top: 14px;
      padding: 4px 0 8px;
      flex-grow: 1;
    }

    .addPaid {
      font-size: 12px;
      color: #12C494;
    }

    .addUnPaid {
      font-size: 12px;
      color: red;
    }

    .remark {
      border-top: $color-text-light 1px solid;
      flex-grow: 1;
      display: flex;
      align-items: end;
      justify-content: center;
      padding-top: 5px;
      z-index: 1000 !important;
      height: auto;

      .remarkInner {
        display: flex;
        justify-content: center;
        align-items: center;

        :global {
          .el-button {
            span {
              display: flex;
              justify-content: center;
              align-items: center;
              overflow-x: auto;

              svg {
                color: $color-primary;
                margin-right: 10px;
              }

              p {
                color: $color-secondary;
              }
            }
          }
        }
      }
    }
  }
}

.outerMainButton {
  height: 50px;
  width: 100%;
  margin-left: -20px;
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 0;

  :global {
    .el-button {
      span {
        display: flex;
        justify-content: center;
        align-items: center;
        overflow-x: auto;

        svg {
          color: $color-primary;
          margin-right: 10px;
        }

        p {
          color: $color-secondary;
        }
      }
    }
  }
}
</style>
